import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { ApiError } from "src/core/apiError";
import { logger } from "src/core/globals";
import { LoginContext } from "src/core/loginContext";
import { MessageManager } from "src/core/streaming/core/messageManager";
import {
  ActivityEvent,
  FabricCandidate,
  FabricConfig,
  GetLimitsResponse,
  TenantObjects
} from "src/gen/schema/configd/api";
import { FabricMultisite } from "src/gen/schema/models/dci";
import { CandidateType, SoftwareMode } from "src/gen/schema/models/fabric";
import { FabricSoftwareConfig } from "src/gen/schema/models/image";
import { Fabric, Vni, Vrf } from "src/gen/schema/models/models";
import { IpSlaConfig } from "src/gen/schema/models/qos";
import { PerVlanStp } from "src/gen/schema/models/routing";
import { FabricTransaction } from "src/gen/schema/schema/schema";
import { DeviceModelHelper, useDeviceModels } from "src/hooks/useDeviceModels";
import { FabricHelpers, useFabricHelpers } from "src/hooks/useFabricHelpers";
import { Sextant } from "src/utils/api";
import { CandidateMode, ConfigD, KEY_NAME } from "src/utils/api/configD";

export const fixContext = () => {
  /**
   * TODO: figure out if there is a better solution
   * This is to bypass a bug
   * with the hot reload. When
   * a file is changed in dev mode
   * the hot reload will reset
   * all context to their default
   * value. this will mostly break
   * any context. To fix the issue
   * forcing a page reload will
   * reset the context back to
   * a working state.
   *
   * This is kind of a sledgehammer
   * for cracking eggs ;)
   */
  window.location.reload();
};

export type CandidateContext = "fabric" | "multisite" | undefined;

interface DataProviderContextSchema extends FabricHelpers, DeviceModelHelper {
  error?: ApiError;
  loading: boolean;
  fabric?: Fabric;
  multiSites?: FabricMultisite[];
  softwareConfig: FabricSoftwareConfig | undefined;
  tenants?: TenantObjects[];
  perVlanStp?: PerVlanStp;
  ipslaConfigs?: IpSlaConfig[];
  events: ActivityEvent[];
  candidateType: CandidateType;
  context: CandidateContext; // Current context: fabric or multisite
  mode: CandidateMode; // Candidate mode for both fabric and multisite
  vnis: Vni[];
  vrfs: Vrf[];
  refresh: () => void;
  setMode: (m: CandidateMode) => void;
  setFabricId: (id?: string) => void;
  setMultisiteId: (id?: string) => void;
  configLimits?: GetLimitsResponse;
}

export const DataProviderContext = createContext<DataProviderContextSchema>({
  error: undefined,
  fabric: undefined,
  softwareConfig: FabricSoftwareConfig.fromPartial({
    softwareMode: SoftwareMode.SOFTWARE_MODE_UNSPECIFIED
  }),
  loading: false,
  events: [],
  candidateType: CandidateType.CANDIDATE_UNSPECIFIED,
  tenants: [],
  ipslaConfigs: [],
  perVlanStp: undefined,
  context: undefined,
  mode: CandidateMode.Edit,
  configLimits: undefined,
  vnis: [],
  vrfs: [],
  multiSites: [],
  /**
   * This is sort of a sledgehammer
   * because the streaming configs
   * don't stream everything we need.
   */
  refresh: fixContext,
  setMode: fixContext,
  setFabricId: fixContext,
  setMultisiteId: fixContext,
  /* FabricHelpers */
  getPortById: () => undefined,
  getPortByName: () => undefined,
  getPortByIndex: () => undefined,
  getNode: () => undefined,
  getPluggableByName: () => undefined,
  /* DeviceModelHelper */
  getDeviceByModelName: () => undefined,
  getDeviceModels: () => [],
  getDeviceModelNames: () => [],
  isBindableDeviceModelName: () => false,
  isVisibleDeviceModelName: () => false
});

type ApiState = {
  controller: AbortController;
  inProgress: boolean;
  pending: boolean;
};

/**
 * Compares two FabricTransaction arrays to determine if any transactions have changed.
 * Returns true if the arrays differ in length or if any transaction's changedAt timestamp differs.
 * @param prev - Previous array of FabricTransactions
 * @param next - New array of FabricTransactions
 * @returns true if transactions have changed, false otherwise
 */
const hasChanged = (prev: FabricTransaction[], next: FabricTransaction[]) => {
  /* If the length of the arrays differ, then something has changed */
  if (prev.length !== next.length) {
    return true;
  }
  /* If any changedAt value is different, then something has changed */
  return prev.some((p, i) => p.changedAt !== next[i]?.changedAt);
};

interface DataProviderProps extends PropsWithChildren {
  /* Added to keep the existing children from needing to change */
  readonly configLimits?: GetLimitsResponse;
}

export const DataProvider = ({ children, configLimits }: DataProviderProps) => {
  /* The list of pending Fabric Transaction */
  const [error, setError] = useState<ApiError>();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [, setFabricTransaction] = useState<FabricTransaction[]>([]);
  /* Type of candidate configuration */
  const [candidateType, setCandidateType] = useState<CandidateType>(
    CandidateType.CANDIDATE_UNSPECIFIED
  );
  /* The full Fabric config */
  const [fabricConfig, setFabricConfig] = useState<FabricConfig | undefined>();

  const [fabricId, setFabricId] = useState<string | undefined>();
  const [multisiteId, setMultisiteId] = useState<string | undefined>();
  const { isReadOnly } = useContext(LoginContext);
  const [mode, _setMode] = useState<CandidateMode>(() => {
    /* Get the value in local storage if there is one. */
    const v = localStorage.getItem(KEY_NAME);
    /**
     * Currently just defaulting to edit, but once
     * we have the changes api the plan is to default
     * to edit if there are pending changes.
     */
    return v ? (v as CandidateMode) : CandidateMode.Edit;
  });
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * Tracks the in-flight fabric request so we don't spam the API.
   * An AbortController cancels the previous request when the fabric
   * changes or the provider unmounts, and two flags coordinate
   * overlapping refresh calls:
   * 1) inProgress - a call is currently running
   * 2) pending - a refresh was requested while a call was running
   *    and should be re-run by the polling interval
   */
  const apiState = useRef<ApiState>({
    controller: new AbortController(),
    inProgress: false,
    pending: false
  });

  /**
   * refresh function that loads the fabric config and pending
   * transactions. To avoid spamming the API, an in-flight call is tracked
   * via apiState.inProgress: if a call is already running, the request is
   * flagged as pending and re-run later by the polling interval instead of
   * firing a concurrent request.
   */
  const refresh = useCallback(() => {
    if (fabricId) {
      /**
       * If a call is already in progress, flag a pending refresh
       * instead of firing a concurrent request. The polling interval
       * will re-run it once the current call finishes.
       */
      if (apiState.current.inProgress) {
        apiState.current.pending = true;
      } else {
        apiState.current.inProgress = true;
        apiState.current.pending = false;
        /**
         * scope the controller to the current functional closure
         */
        const controller = apiState.current.controller;
        /**
         * simple helper to detect if the abort controller
         * has been changed, this avoids code duplication
         * of controller === apiState.current.controller
         */
        const isCurrent = () => controller === apiState.current.controller;
        /**
         * options so the api calls can be aborted if the fabric changes
         * or the provider unmounts
         */
        const options = { signal: controller.signal };
        Promise.all([
          ConfigD.getFabricConfig(mode, fabricId, false, options).then(
            (fc: FabricConfig | undefined) => {
              if (fc && isCurrent()) {
                setFabricConfig(fc);
                setError(undefined);
              }
            },
            (err: ApiError) => {
              if (isCurrent()) {
                setError(err);
              }
            }
          ),
          /**
           * Loads all the pending Fabric Transactions.
           * There should just be one Candidate with a
           * bunch of activity events
           */
          ConfigD.getFabricTransactions(fabricId, undefined, options).then(
            (t: FabricCandidate[] = []) => {
              if (!isCurrent()) {
                return;
              }
              if (t[0]) {
                const [candidate] = t;
                setEvents(candidate.events);
                setCandidateType(candidate.type);
              } else {
                setEvents([]);
                setCandidateType(CandidateType.CANDIDATE_UNSPECIFIED);
              }
            },
            (err: ApiError) => {
              if (isCurrent()) {
                setError(err);
              }
            }
          )
        ]).finally(() => {
          if (isCurrent()) {
            apiState.current.inProgress = false;
            setLoading(false);
          }
        });
      }
    }
  }, [fabricId, mode]);

  useEffect(() => {
    const id = setInterval(() => {
      if (apiState.current.pending) {
        refresh();
      }
    }, 1000);

    refresh();
    window.cnc.setSync(refresh);

    return () => clearInterval(id);
  }, [refresh]);

  /**
   * This will subscribe to the FabricTransaction thread list.
   * It should trigger an updated when anything on the fabric
   * changes.
   */
  useEffect(() => {
    if (fabricId) {
      return Sextant.subscribeFabricTransactions(
        fabricId,
        (transactions: FabricTransaction[]) => {
          setFabricTransaction((prev) => {
            /**
             * To avoid over-fetching the same data, verify that the
             * last transaction has a change, only refresh if there
             * are new transactions.
             */
            if (hasChanged(prev, transactions)) {
              /**
               * Trigger a refresh to load the latest
               * config
               */
              refresh();
              return transactions;
            }
            return prev;
          });
        },
        (err) => logger.error(err)
      );
    }
    return MessageManager.unsubscribe;
  }, [fabricId, refresh]);

  useEffect(() => {
    // Connect the fabric specific websocket
    window.cnc.sextantPool.useStream(fabricId);
    return () => {
      apiState.current.controller.abort();
      /**
       * reset api state
       */
      apiState.current = {
        controller: new AbortController(),
        inProgress: false,
        pending: false
      };
      /**
       * TORT-10920 UI: "No fabric data found" page is
       * shown up before loading the fabric page.
       *
       * Need to reset the loading flag for each
       * fabric or the "No fabric data found" error
       * us shown while loading.
       */
      setLoading(true);
      setFabricConfig(undefined);
      /**
       * websocket will disconnect itself once
       * all the subscribers are cleaned up.
       * should be no need for
       * window.cnc.sextantPool.close(fabricId);
       */
    };
  }, [fabricId]);

  const {
    getNode,
    getPortById,
    getPortByIndex,
    getPortByName,
    getPluggableByName
  } = useFabricHelpers(fabricConfig?.fabric);

  const {
    getDeviceByModelName,
    getDeviceModelNames,
    getDeviceModels,
    isBindableDeviceModelName,
    isVisibleDeviceModelName
  } = useDeviceModels();

  const setMode = useCallback(
    (m: CandidateMode) => {
      if (isReadOnly) {
        _setMode(CandidateMode.Running);
        return;
      }
      /* store the value in local storage so the user will have a consistent experience */
      localStorage.setItem(KEY_NAME, m);
      _setMode(m);
    },
    [isReadOnly]
  );

  // Determine the current context based on which ID is set
  const context: CandidateContext = useMemo(() => {
    if (fabricId) {
      return "fabric";
    } else if (multisiteId) {
      return "multisite";
    }
    return undefined;
  }, [fabricId, multisiteId]);

  // Array of VNI objects from the first tenant (excludes L3 VNIs)
  const vnis = useMemo(() => {
    return fabricConfig?.tenants?.[0]?.vnis.filter((vni) => !vni.isL3) ?? [];
  }, [fabricConfig?.tenants]);

  // Array of VRF objects from the first tenant
  const vrfs = useMemo(() => {
    return fabricConfig?.tenants?.[0]?.vrfs ?? [];
  }, [fabricConfig?.tenants]);

  const value = useMemo(
    () => ({
      error,
      loading,
      fabric: fabricConfig?.fabric,
      multiSites: fabricConfig?.multisites,
      softwareConfig: fabricConfig?.swConf,
      tenants: fabricConfig?.tenants,
      ipslaConfigs: fabricConfig?.tenants?.[0]?.ipSlaConfigs,
      perVlanStp: fabricConfig?.perVlanStp,
      events,
      candidateType,
      context,
      mode,
      vnis,
      vrfs,
      /**
       * This should not be needed but
       * the reality is the streaming api
       * doesn't stream everything we need.
       * this will let a component force a
       * reload of the fabric config.
       */
      refresh,
      setMode,
      setFabricId,
      setMultisiteId,
      configLimits,
      /* Node & Port Helper functions */
      getNode,
      getPortByName,
      getPortByIndex,
      getPortById,
      getPluggableByName,
      /* Device Model Helper functions */
      getDeviceByModelName,
      getDeviceModelNames,
      getDeviceModels,
      isBindableDeviceModelName,
      isVisibleDeviceModelName
    }),
    [
      error,
      candidateType,
      configLimits,
      context,
      events,
      fabricConfig?.fabric,
      fabricConfig?.multisites,
      fabricConfig?.perVlanStp,
      fabricConfig?.swConf,
      fabricConfig?.tenants,
      getDeviceByModelName,
      getDeviceModelNames,
      getDeviceModels,
      getNode,
      getPluggableByName,
      getPortById,
      getPortByIndex,
      getPortByName,
      loading,
      mode,
      refresh,
      setMode,
      vnis,
      vrfs,
      isBindableDeviceModelName,
      isVisibleDeviceModelName
    ]
  );

  return (
    <DataProviderContext.Provider value={value}>
      {children}
    </DataProviderContext.Provider>
  );
};
